import { DelegationData } from 'src/app/shared/models/delegation-data.model';
import { XsellOffer, XsellOfferConsent } from '../../offers/models/model';
import { ArchiveStatus } from './archive-status';
import { BillerStatus } from './biller-status.enum';
import { CustomerBillerStatus } from './customer-biller-status.model';
import { SubAccount } from './sub-account.model';
import { Tag } from './tag.model';
import { IBasicCredentialsForm, ICredentialsFormData, IFollowUpCredentialsForm } from './credential-form.model';

export interface IBiller {
  initial: string;
  additionalHint?: string;
  aliases?: string[];
  categories?: BillerCategory[];
  credentialsForms?: IBasicCredentialsForm[];
  code?: string;
  defaultEmailSender?: string;
  gmailEnabled?: boolean;
  importantHint?: string;
  name: string;
  companyName?: string;
  shortName?: string;
  rank?: number;
  scraperEnabled?: boolean;
  status?: BillerStatus;
  url?: string;
  nip?: string;
}

export enum BillerSearchStatusEnum {
  'COMPLETED' = 'COMPLETED',
  'CREATED' = 'CREATED',
  'FAILED' = 'FAILED',
  'STARTED' = 'STARTED',
}

export enum EmailAccountStatusEnum {
  'ENABLED' = 'ENABLED',
  'INVALID_CREDENTIAL' = 'INVALID_CREDENTIAL',
  'USER_ACTION_NEEDED' = 'USER_ACTION_NEEDED',
  'VERIFYING' = 'VERIFYING',
}

export type BillerCategory =
  | 'B2B'
  | 'Czynsz'
  | 'E-commerce'
  | 'Energia'
  | 'Gaz'
  | 'Hosting'
  | 'Inne'
  | 'Internet'
  | 'Księgowość'
  | 'Ochrona'
  | 'Telefon'
  | 'Telewizja'
  | 'Ubezpieczenia'
  | 'Woda'
  | 'Zdrowie';

export enum LiabilitySource {
  EMAIL = 'EMAIL',
  EMAIL_FORWARD = 'EMAIL_FORWARD',
  MANUAL_SCANNING = 'MANUAL_SCANNING',
  MANUAL = 'MANUAL',
  PAY_BY_LINK = 'PAY_BY_LINK',
  USERPANEL_SCRAPER = 'USERPANEL_SCRAPER',
  DELEGATED = 'DELEGATED',
  TOP_UP = 'TOP_UP',
}

export class EmailAccess {
  public draftId: string;
  public emailAccount: IEmailAccount;
  public id: string;
  public updatedAt: Date;
}

export type EmailAccountStatus = 'ENABLED' | 'INVALID_CREDENTIAL';

export type EmailAccountType = 'GMAIL' | 'IMAP';

export interface IEmailAccount {
  emailAccountId?: string;
  emailAddress: string;
  host: string;
  password?: string;
  port: number;
  sslEnabled: boolean;
  status: EmailAccountStatus;
  type: EmailAccountType;
}

export interface IBillerShort {
  id: string;
  name: string;
}

export interface IEmailConfiguration {
  code: string;
  displayName: string;
  host: string;
  port: number;
  sslEnabled: boolean;
  status?: EmailAccountStatus;
}

export interface CustomerBiller {
  activeDraftLiabilitySource: DraftLiabilitySource;
  automaticPaymentAvailable: boolean;
  automaticPaymentEditable: boolean;
  automaticPaymentId: string;
  biller: IBiller;
  createdAt?: Date;
  customerId: string;
  displayName?: string;
  draftLiabilitySources: DraftLiabilitySource[];
  emailAccess?: EmailAccess;
  id: string;
  inboxEmailAddress?: string;
  liabilityCount?: number;
  liabilitySource?: LiabilitySource;
  manualConfiguration?: ManualConfiguration;
  needsAttention?: boolean;
  showXsellConsentBtn: boolean;
  status: CustomerBillerStatus;
  subAccounts: SubAccount[];
  subAccountsFolded: boolean;
  syncRequestAvailable: boolean;
  syncRequestAt: Date;
  userpanelAccess?: UserpanelAccess;
  verified: boolean;
  xsellOffer?: XsellOffer;
  xsellOfferConsent?: XsellOfferConsent;
  tags: Tag[];
  archiveStatus: ArchiveStatus;
  delegationData: DelegationData;
  btApiAccountId?: string;
  topUpPhoneNumber: string;
}

export class DraftLiabilitySource {
  public emailAccess: EmailAccess;
  public id: string;
  public liabilitySource: LiabilitySource;
  public manualConfiguration: ManualConfiguration;
  public status: CustomerBillerStatus;
  public userPanelAccess: UserpanelAccess;
}

export class UserpanelAccess {
  public draftId: string;
  public id: string;
  public updatedAt: Date;
  public username: string;
  public uanMessage: UanMessage;
  public followUpCredentialsForm?: IFollowUpCredentialsForm;
  public credentialsFormData?: ICredentialsFormData;
}

export class UanMessage {
  public shortMessage: string;
  public longMessage: string;
}

export type ConsentSource = 'BILLER_SEARCH' | 'EMAIL_ACCESS';

export class ManualConfiguration {
  public amount: number;
  public bban: string;
  public draftId: string;
  public firstPaymentDate: Date;
  public id: string;
  public recurring: boolean | null;
  public scheduleRate: ScheduleRate;
  public showTitleSuffix: boolean;
  public title: string;
}

export type ScheduleRate = 'WEEK' | 'TWO_WEEKS' | 'MONTH' | 'TWO_MONTHS' | 'QUARTER' | 'HALF_A_YEAR' | 'YEAR';

export interface ICanDeleteCustomerBiller {
  blockingPaymentId: string;
  canDelete: boolean;
}

export interface TranslatedValue {
  PL: string;
  EN: string;
}

export interface BbansForBillerInVerification {
  id: string;
  billerId: string;
  billerName: string;
  bban;
  string;
  items: LiabilityInVerification[];
  verified: boolean | null;
}

export interface BbansForNipInVerification {
  id: string;
  nip: string;
  bban: string;
  items: LiabilityInVerification[];
  billerNames: string;
  verified: boolean | null;
}

export interface Company {
  id?: string;
  nip: string;
  name: string;
  shortName: string;
  verified: boolean | null;
  bbans?: BbansForNipInVerification[];
}
export enum VerificationFilter {
  ALL = 'ALL',
  TO_VERIFICATION = 'TO_VERIFICATION',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}


export interface LiabilityInVerification {
  billerId: string;
  name: string;
  customerId: string;
  nick: string;
  title: string;
  email: string;
  date: Date;
  amount: number;
}
